import { useEffect, useRef, useState } from "react";
import { Loader2, Scissors, User, CreditCard, Fingerprint, SlidersHorizontal, Camera, X } from "lucide-react";
import { toast } from "sonner";
import NumField from "@/components/NumField";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELD =
  "focus-ring h-11 w-full rounded-xl border border-[#E1E1E6] bg-white px-3.5 text-[15px] placeholder:text-[#B0B0B8]";

const EMPTY = {
  nama: "", nik: "", jabatan: "", tanggal_lahir: "", tahun_masuk: "", rate_harian: 0,
  metode: "transfer", bank: "", no_rekening: "", atas_nama: "", no_hp: "",
  bpjs: false, eligible_bonus: true, aktif: true, is_jarit: false, verified: false,
  payment_method: "", nama_rekening: "", bca_transfer_type: "",
  receiver_cust_type: "1", receiver_cust_residence: "1", pay_to_employee_id: "",
  fingerspot_pin: "",
};

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
    <label className="block text-[12.5px] font-medium text-[#6E6E73]">{label}</label>
    {children}
  </div>
);

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="border-t border-black/[0.06] pt-6 first:border-0 first:pt-0">
    <div className="mb-4 flex items-center gap-2.5">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-[#EEF3FA] text-[#0071E3]"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0">
        <h4 className="text-[14px] font-semibold tracking-tight text-[#1D1D1F]">{title}</h4>
        {desc && <p className="text-[11.5px] leading-snug text-[#8E8E93]">{desc}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Toggle = ({ label, desc, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-4 rounded-2xl border border-[#E1E1E6] bg-white px-4 py-3">
    <div className="min-w-0">
      <p className="text-[14px] font-medium text-[#1D1D1F]">{label}</p>
      {desc && <p className="text-[11.5px] text-[#8E8E93]">{desc}</p>}
    </div>
    <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
  </div>
);

// Shared employee create/edit form (no modal). Used by the New Employee page
// and the inline Edit mode on the Employee profile page.
export const EmployeeForm = ({ initial, isAdmin, isNew, banks = [], employees = [], busy, onSubmit, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");

  useEffect(() => {
    if (initial) return;
    api.get("/settings/payroll-defaults").then((r) => {
      const d = r.data || {};
      setForm((f) => ({ ...f, rate_harian: f.rate_harian || d.rate_harian || 0, bpjs: d.bpjs ?? f.bpjs }));
    }).catch(() => {});
  }, [initial]);
  const fileRef = useRef(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isJarit = !!form.is_jarit;

  const pickPhoto = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("File harus berupa gambar"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Ukuran foto maksimal 8MB"); return; }
    setPhotoFile(f);
    setPhotoUrl(URL.createObjectURL(f));
  };
  const clearPhoto = () => { setPhotoFile(null); setPhotoUrl(""); };

  const submit = () => {
    if (!form.nama.trim()) { toast.error("Nama wajib diisi"); return; }
    const payload = {
      nama: form.nama.trim(),
      nik: (form.nik || "").trim(),
      jabatan: form.jabatan || "",
      tanggal_lahir: form.tanggal_lahir || "",
      tahun_masuk: (form.tahun_masuk || "").toString().trim(),
      rate_harian: Number(form.rate_harian) || 0,
      metode: form.metode || "transfer",
      bank: form.metode === "transfer" ? form.bank || "" : "",
      no_rekening: form.metode === "transfer" ? form.no_rekening || "" : "",
      atas_nama: form.metode === "transfer" ? form.atas_nama || form.nama.trim() : "",
      no_hp: (form.no_hp || "").trim(),
      bpjs: !!form.bpjs,
      eligible_bonus: !!form.eligible_bonus,
      aktif: !!form.aktif,
      is_jarit: !!form.is_jarit,
      verified: !!form.verified,
      payment_method: form.payment_method || "",
      nama_rekening: form.metode === "transfer" ? (form.nama_rekening || "").trim() : "",
      bca_transfer_type: form.metode === "transfer" ? form.bca_transfer_type || "" : "",
      receiver_cust_type: form.receiver_cust_type || "1",
      receiver_cust_residence: form.receiver_cust_residence || "1",
      pay_to_employee_id: form.pay_to_employee_id || "",
    };
    onSubmit(payload, form.fingerspot_pin || "", photoFile);
  };

  return (
    <div data-testid="employee-form" className="space-y-6 sm:space-y-7">
      {/* Photo (new employee only; existing employees manage photo in the profile header) */}
      {isNew && (
        <div className="border-b border-black/[0.06] pb-6">
          <div className="flex items-center gap-4 sm:gap-5">
            <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full bg-[#F2F2F7] ring-1 ring-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.08)] sm:h-24 sm:w-24">
              {photoUrl ? (
                <img data-testid="emp-photo-preview" src={photoUrl} alt="Preview" className="h-full w-full object-cover" />
              ) : (
                <span className="grid h-full w-full place-items-center text-[#B0B0B8]"><Camera className="h-7 w-7" /></span>
              )}
            </div>
            <div className="min-w-0">
              <h4 className="text-[14px] font-semibold tracking-tight text-[#1D1D1F]">Foto Karyawan</h4>
              <p className="mb-2.5 text-[11.5px] leading-snug text-[#8E8E93]">Opsional. JPG / PNG, maks 8MB.</p>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="emp-photo-input"
                onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" data-testid="emp-photo-upload" onClick={() => fileRef.current?.click()} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[#EEF3FA] px-3.5 text-[13px] font-medium text-[#0071E3] transition-colors hover:bg-[#E2ECF8]">
                  <Camera className="h-4 w-4" /> {photoUrl ? "Ganti Foto" : "Upload Foto"}
                </button>
                {photoUrl && (
                  <button type="button" data-testid="emp-photo-clear" onClick={clearPhoto} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[#FDECEC] px-3.5 text-[13px] font-medium text-[#C0362C] transition-colors hover:bg-[#FBDDDD]">
                    <X className="h-4 w-4" /> Hapus
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Type */}
      <Section icon={isJarit ? Scissors : User} title="Tipe Karyawan" desc="Menentukan cara perhitungan gaji">
        {isNew ? (
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { v: false, label: "Harian", desc: "Gaji dihitung per hari kerja", icon: User },
              { v: true, label: "Penjahit (Piece Rate)", desc: "Ongkos per potong jahitan", icon: Scissors },
            ].map((t) => {
              const active = isJarit === t.v;
              return (
                <button
                  key={String(t.v)}
                  type="button"
                  data-testid={`emp-type-${t.v ? "jarit" : "harian"}`}
                  onClick={() => set("is_jarit")(t.v)}
                  className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-all ${active ? "border-[#0071E3] bg-[#EEF3FA] ring-2 ring-[#0071E3]/30" : "border-[#E1E1E6] bg-white hover:border-[#C7D6EA] hover:bg-[#FAFBFD]"}`}
                >
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl transition-colors ${active ? "bg-[#0071E3] text-white" : "bg-[#F2F2F7] text-[#8E8E93]"}`}>
                    <t.icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <p className="text-[14.5px] font-semibold text-[#1D1D1F]">{t.label}</p>
                    <p className="text-[11.5px] leading-snug text-[#8E8E93]">{t.desc}</p>
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-medium ${isJarit ? "bg-[#FBEEF0] text-[#C8102E]" : "bg-[#EEF3FA] text-[#0071E3]"}`}>
            {isJarit ? <Scissors className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
            {isJarit ? "Penjahit (Piece Rate)" : "Harian"}
          </span>
        )}
      </Section>

      {/* Personal */}
      <Section icon={User} title="Informasi Pribadi" desc="Identitas & kontak karyawan">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <F label="Nama Lengkap" full>
            <input data-testid="emp-field-nama" value={form.nama} onChange={(e) => set("nama")(e.target.value)} placeholder="mis. Siti Rahayu" className={FIELD} />
          </F>
          <F label="NIK / No. KTP">
            <input data-testid="emp-field-nik" value={form.nik || ""} onChange={(e) => set("nik")(e.target.value)} placeholder="51xxxxxxxxxxxxxx" className={`${FIELD} num`} />
          </F>
          <F label="Tanggal Lahir">
            <input data-testid="emp-field-birthday" type="date" value={form.tanggal_lahir || ""} onChange={(e) => set("tanggal_lahir")(e.target.value)} className={`${FIELD} num`} />
          </F>
          <F label="Tahun Bergabung">
            <input data-testid="emp-field-tahun-masuk" value={form.tahun_masuk || ""} onChange={(e) => set("tahun_masuk")(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} inputMode="numeric" placeholder="mis. 2021" className={`${FIELD} num`} />
          </F>
          <F label="Jabatan">
            <input data-testid="emp-field-jabatan" value={form.jabatan || ""} onChange={(e) => set("jabatan")(e.target.value)} placeholder="mis. Staff Produksi" className={FIELD} />
          </F>
          <F label="No. HP (WhatsApp)">
            <input data-testid="emp-field-nohp" value={form.no_hp || ""} onChange={(e) => set("no_hp")(e.target.value)} placeholder="08123456789" className={`${FIELD} num`} />
          </F>
          {isAdmin && !isJarit && (
            <F label="Rate / Hari">
              <NumField testid="emp-field-rate" value={form.rate_harian || 0} onChange={set("rate_harian")} align="text-left" className="h-11 w-full border-[#E1E1E6] bg-white text-[15px]" />
            </F>
          )}
        </div>
      </Section>

      {/* Payment & bank */}
      <Section icon={CreditCard} title="Pembayaran & Bank" desc="Metode transfer / tunai dan detail rekening">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <F label="Metode Pembayaran" full>
            <Select value={form.metode || "transfer"} onValueChange={set("metode")}>
              <SelectTrigger data-testid="emp-field-metode" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[15px]"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl"><SelectItem value="transfer">Transfer Bank</SelectItem><SelectItem value="tunai">Tunai (Cash)</SelectItem></SelectContent>
            </Select>
          </F>

          {form.metode === "transfer" && (
            <>
              <F label="Bank">
                <input data-testid="emp-field-bank" list="emp-bank-list" value={form.bank || ""} onChange={(e) => set("bank")(e.target.value)} placeholder="Ketik / pilih bank" className={FIELD} />
                <datalist id="emp-bank-list">{banks.map((b) => <option key={b.id} value={b.display_name} />)}</datalist>
              </F>
              <F label="No. Rekening">
                <input data-testid="emp-field-rekening" value={form.no_rekening || ""} onChange={(e) => set("no_rekening")(e.target.value)} className={`${FIELD} num`} />
              </F>
              <F label="Nama Pemilik Rekening" full>
                <input data-testid="emp-field-atasnama" value={form.atas_nama || ""} onChange={(e) => set("atas_nama")(e.target.value)} className={FIELD} />
              </F>
              <F label="Nama sesuai rekening — BCA (opsional)" full>
                <input data-testid="emp-field-namarek" value={form.nama_rekening || ""} onChange={(e) => set("nama_rekening")(e.target.value)} placeholder="Kosongkan = pakai Nama Pemilik Rekening" className={FIELD} />
              </F>
              <F label="BCA Transfer Type">
                <Select value={form.bca_transfer_type || "default"} onValueChange={(v) => set("bca_transfer_type")(v === "default" ? "" : v)}>
                  <SelectTrigger data-testid="emp-field-bcatype" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[15px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-xl"><SelectItem value="default">Default (per bank)</SelectItem><SelectItem value="LLG">LLG</SelectItem><SelectItem value="RTG">RTG (RTGS)</SelectItem></SelectContent>
                </Select>
              </F>
              <div className="grid grid-cols-2 gap-3 sm:col-span-2">
                <F label="Cust Type">
                  <Select value={form.receiver_cust_type || "1"} onValueChange={set("receiver_cust_type")}>
                    <SelectTrigger data-testid="emp-field-custtype" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[15px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl"><SelectItem value="1">1 — Personal</SelectItem><SelectItem value="2">2 — Corporate</SelectItem><SelectItem value="3">3 — Government</SelectItem></SelectContent>
                  </Select>
                </F>
                <F label="Residence">
                  <Select value={form.receiver_cust_residence || "1"} onValueChange={set("receiver_cust_residence")}>
                    <SelectTrigger data-testid="emp-field-residence" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[15px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl"><SelectItem value="1">1 — Resident</SelectItem><SelectItem value="2">2 — Non-resident</SelectItem></SelectContent>
                  </Select>
                </F>
              </div>
            </>
          )}

          <F label="Metode Payroll BCA (override)" full>
            <Select value={form.payment_method || "auto"} onValueChange={(v) => set("payment_method")(v === "auto" ? "" : v)}>
              <SelectTrigger data-testid="emp-field-paymethod" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[15px]"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl"><SelectItem value="auto">Auto (deteksi dari bank + rekening)</SelectItem><SelectItem value="BANK_TRANSFER">Paksa Transfer Bank</SelectItem><SelectItem value="CASH">Paksa Tunai (keluarkan dari file BCA)</SelectItem></SelectContent>
            </Select>
          </F>

          <F label="Gaji dibayarkan ke" full>
            <Select value={form.pay_to_employee_id || "self"} onValueChange={(v) => set("pay_to_employee_id")(v === "self" ? "" : v)}>
              <SelectTrigger data-testid="emp-field-payto" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[15px]"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="self">Rekening sendiri</SelectItem>
                {employees.filter((e) => e.id !== initial?.id).map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.nama}{e.emp_code ? ` (${e.emp_code})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
        </div>
      </Section>

      {/* Fingerspot (admin) */}
      {isAdmin && (
        <Section icon={Fingerprint} title="Absensi Fingerspot" desc="Pemetaan PIN device (opsional)">
          <div className="rounded-2xl border border-[#E1E1E6] bg-white p-4">
            <div className="mb-2.5 flex items-center justify-between">
              <span className="text-[13px] font-medium text-[#6E6E73]">Device PIN mapping</span>
              <span data-testid="emp-field-fs-status" className={`rounded-full px-2.5 py-0.5 text-[11.5px] font-medium ${form.fingerspot_pin ? "bg-[#E9F9EF] text-[#0E8A46]" : "bg-[#F1F1F4] text-[#8E8E93]"}`}>{form.fingerspot_pin ? "Linked" : "Not Linked"}</span>
            </div>
            <input data-testid="emp-field-fs-pin" value={form.fingerspot_pin || ""} onChange={(e) => set("fingerspot_pin")(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="Fingerspot PIN (mis. 1)" className={FIELD} />
          </div>
        </Section>
      )}

      {/* Status */}
      <Section icon={SlidersHorizontal} title="Status & Opsi">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Toggle label="BPJS Ketenagakerjaan" desc="Potong iuran BPJS tiap bulan" checked={form.bpjs} onChange={set("bpjs")} testid="emp-field-bpjs" />
          {isAdmin && <Toggle label="Auto Bonus" desc="Masuk perhitungan bonus otomatis" checked={form.eligible_bonus} onChange={set("eligible_bonus")} testid="emp-field-bonus" />}
          {isAdmin && <Toggle label="Verified" desc="Tampilkan lencana terverifikasi di profil" checked={form.verified} onChange={set("verified")} testid="emp-field-verified" />}
          <Toggle label="Karyawan Aktif" desc="Tampil di payroll berjalan" checked={form.aktif} onChange={set("aktif")} testid="emp-field-aktif" />
        </div>
      </Section>

      {/* Actions */}
      <div className="flex flex-col-reverse gap-2.5 border-t border-black/[0.06] pt-5 sm:flex-row">
        {onCancel && (
          <button data-testid="emp-form-cancel" onClick={onCancel} disabled={busy} className="h-12 rounded-xl border border-[#E1E1E6] bg-white text-[15px] font-medium text-[#6E6E73] transition-colors hover:bg-[#FAFAFA] disabled:opacity-50 sm:h-11 sm:flex-1">Batal</button>
        )}
        <button data-testid="emp-form-save" onClick={submit} disabled={busy} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#0071E3] text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,113,227,0.28)] transition-all hover:bg-[#0077ED] active:scale-[0.99] disabled:opacity-50 sm:h-11 sm:flex-[2]">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isNew ? "Tambah Karyawan" : "Simpan Perubahan"}
        </button>
      </div>
    </div>
  );
};

export default EmployeeForm;
